import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { FaDownload, FaInfoCircle } from 'react-icons/fa';
import { AuthContext } from '../../../components/AuthContext';
import LoadingSpinner from '../../../components/LoadingSpinner';
import EnhancedLoadingIndicator from '../../../components/EnhancedLoadingIndicator';
import Modal from '../../../components/Modal';
import Swal from 'sweetalert2';

// Helper function to format dates consistently
const formatDate = (dateString) => {
  try {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// Format money values
const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};
  
// Format points with commas
const formatPoints = (points) => {
  const numPoints = Number(points || 0);
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    useGrouping: true
  }).format(numPoints);
};

// Helper function to get stock group name
const getStockGroupName = (stockGroup) => {
  if (!stockGroup) return 'Default';
  if (typeof stockGroup === 'string') {
    // Handle cases where stockGroup is "N/A", "Unknown", etc.
    if (stockGroup === 'N/A' || stockGroup === 'Unknown' || stockGroup === '') {
      return 'Default';
    }
    return stockGroup;
  }
  if (stockGroup.Name) return stockGroup.Name;
  if (stockGroup.name) return stockGroup.name;
  return 'Default';
};

// Helper function to show alerts
const showAlert = (type, message) => {
  const swalIcon = type === 'success' ? 'success' :
    type === 'warning' ? 'warning' :
    type === 'error' ? 'error' : 'info';

  Swal.fire({
    icon: swalIcon,
    title: type.charAt(0).toUpperCase() + type.slice(1),
    text: message,
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });
};

// Add safe conversion utilities at the top of the component
const safeString = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object') {
    if (value.toString && typeof value.toString === 'function') {
      return value.toString();
    }
    return JSON.stringify(value);
  }
  return String(value);
};


const safeNumber = (value) => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

// Modern Pagination Component for both report types
const renderModernPagination = (currentPage, totalPages, onPageChange, isLoading = false) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center mt-6 mb-4">
        <div className="flex items-center space-x-2 px-4 py-2 bg-gray-100 rounded-lg">
          <div className="w-6 h-2.5 bg-gray-300 animate-pulse rounded"></div>
          <div className="w-6 h-2.5 bg-gray-400 animate-pulse rounded"></div>
          <div className="w-6 h-2.5 bg-gray-300 animate-pulse rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 mb-4 px-4">
      <div className="text-sm text-gray-700">
        Page {currentPage} of {totalPages}
      </div>
      
      <div className="flex items-center space-x-1">
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors duration-200 ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
              : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400'
          }`}
        >
          Previous
        </button>

        {/* Page Numbers */}
        {getPageNumbers().map(pageNum => (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors duration-200 ${
              currentPage === pageNum
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400'
            }`}
          >
            {pageNum}
          </button>
        ))}

        {/* Next Button */}
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors duration-200 ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
              : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400'
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

// Progress Bar Component for Dealer Performance Loading
const renderProgressBar = (isVisible, progress = 0, message = 'Loading...') => {
  if (!isVisible) return null;

  return (
    <div className="my-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center space-x-3">
        <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-blue-900">{message}</span>
            <span className="text-sm text-blue-700">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CampaignsReports() {
  const { user } = useContext(AuthContext);
  
  // Core data states
  const [dealerData, setDealerData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [summary, setSummary] = useState({});
  
  // Domain-based filtering states
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false);
  const [hasTriedFetchingCompanies, setHasTriedFetchingCompanies] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  
  // Loading and progress states
  const [loading, setLoading] = useState(false);
  const [loadingTotalPoints, setLoadingTotalPoints] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingStockGroups, setLoadingStockGroups] = useState(false);
  const [apiRequestState, setApiRequestState] = useState('idle');
  const [requestProgress, setRequestProgress] = useState(0);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stockGroupPage, setStockGroupPage] = useState(1);
  const [stockGroupPagination, setStockGroupPagination] = useState({});
  const [stockGroupLimit] = useState(10);
  
  // Period and report type states
  const [period, setPeriod] = useState('campaign_duration');
  const [reportType, setReportType] = useState('dealer_performance');
  
  // Modal and detail states
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [dealerDetails, setDealerDetails] = useState(null);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  
  // Export states
  const [exporting, setExporting] = useState(false);
  const [exportingDetails, setExportingDetails] = useState(false);
  
  // Additional data states
  const [activeLedgers, setActiveLedgers] = useState([]);
  const [activeVouchers, setActiveVouchers] = useState([]);
  const [stockGroups, setStockGroups] = useState([]);
  const [associatedDealers, setAssociatedDealers] = useState([]);
  const [error, setError] = useState('');
  
  // Stock group specific states
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [stockGroupData, setStockGroupData] = useState([]);
  const [stockGroupRules, setStockGroupRules] = useState([]);
  const [stockGroupColumns, setStockGroupColumns] = useState([]);
  const [stockGroupSummary, setStockGroupSummary] = useState({});
  const [selectedStockGroupDetails, setSelectedStockGroupDetails] = useState(null);
  const [selectedStockGroupItems, setSelectedStockGroupItems] = useState(null);
  const [showStockGroupDealersModal, setShowStockGroupDealersModal] = useState(false);
  const [showStockGroupItemsModal, setShowStockGroupItemsModal] = useState(false);

  // Add new state for unified modal
  const [showUnifiedDealerModal, setShowUnifiedDealerModal] = useState(false);
  const [unifiedModalView, setUnifiedModalView] = useState('details'); // 'details', 'vouchers', 'pointBreakdown', 'pointsDetails'

  // Add new state for dealer points details
  const [dealerPointsDetails, setDealerPointsDetails] = useState(null);
  const [loadingPointsDetails, setLoadingPointsDetails] = useState(false);

  // Add new state for stock group details modal
  const [showStockGroupDetailsModal, setShowStockGroupDetailsModal] = useState(false);

  // Add new states for specific dealer search modal
  const [showDealerSearchModal, setShowDealerSearchModal] = useState(false);
  const [dealerSearchQuery, setDealerSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [selectedSearchDealer, setSelectedSearchDealer] = useState(null);

  // Fix the API instance configuration
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL, // Use relative path instead of environment variable
    timeout: 3000000,
    headers: {
      'Content-Type': 'application/json',
    }
  });

  // Add request interceptor to include auth token
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Enhanced loading state management for total points
  const manageLoadingState = (pointsCount) => {
    if (pointsCount > 0) {
      // Ensure animation is displayed if points are being calculated
      if (!loadingTotalPoints) {
        console.log(`Points calculation in progress (${pointsCount} remaining), activating animation`);
        setLoadingTotalPoints(true);
        setApiRequestState('processing');
        setRequestProgress(95);
        return true;
      }
    } else if (loadingTotalPoints) {
      // Transition to complete state when points calculation is finished
      console.log('Points calculation complete, stopping animation');
      setApiRequestState('complete');
      setRequestProgress(100);
      setTimeout(() => {
        setLoadingTotalPoints(false);
        setApiRequestState('idle');
        setRequestProgress(0);
      }, 1000);
      return false;
    }
    return loadingTotalPoints;
  };

  // Fix the handlePageChange function for stock group pagination
  const handlePageChange = (newPage) => {
    if (reportType === 'stock_group_points') {
      handleStockGroupPageChange(newPage);
    } else {
      if (newPage === currentPage) return;
      console.log(`Changing dealer page from ${currentPage} to ${newPage}`);
      setCurrentPage(newPage);
      fetchCombinedDealerData(newPage);
    }
  };

  const handleStockGroupPageChange = (newPage) => {
    if (newPage === stockGroupPage) return;
    
    console.log(`Changing stock group page from ${stockGroupPage} to ${newPage}`);
    setStockGroupPage(newPage);
    fetchStockGroupData(newPage);
  };
  
  // Date range effect for campaign selection
  useEffect(() => {
    if (selectedCampaign && campaigns.length > 0) {
      const campaign = campaigns.find(c => c._id === selectedCampaign);
      if (campaign) {
        setDateRange({
          from: formatDate(campaign.date_from),
          to: formatDate(campaign.date_to)
        });
      }
    }
  }, [selectedCampaign, campaigns]);
  
  useEffect(() => {
    if (!user) return;

    const userData = user.user || user;
    
    // Reset the flag when user changes
    setHasTriedFetchingCompanies(false);
    setAvailableCompanies([]);
    
    console.log('CampaignsReports: Initializing for Admin user with intersection-based access');
    
    // For Admin users, use intersection-based access (admin assigned + domain matching)
    if (user.role === 'Admin' && userData && !hasTriedFetchingCompanies) {
      fetchAdminCompanies();
    } else if (userData && userData.company) {
      // For other user types with direct company assignment
      fetchCompanies();
      const id = typeof userData.company === 'object' ? userData.company._id : userData.company;
      setSelectedCompany(id);
    } else {
      // For other cases, fetch all companies
      fetchCompanies();
    }
  }, [user, hasTriedFetchingCompanies]); // Clean dependency array

  // Handle company selection after companies are fetched
  useEffect(() => {
    if (availableCompanies.length > 0 && !selectedCompany) {
      if (availableCompanies.length === 1) {
        setSelectedCompany(availableCompanies[0]._id);
      }
      // If multiple companies, let user select manually
    }
  }, [availableCompanies, selectedCompany]);
  // Update the body scroll lock useEffect to include the new modal
  useEffect(() => {
    const isAnyModalOpen = showUnifiedDealerModal || showStockGroupDealersModal || showStockGroupItemsModal || showStockGroupDetailsModal || showDealerSearchModal;
    
    if (isAnyModalOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on component unmount
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    };
  }, [showUnifiedDealerModal, showStockGroupDealersModal, showStockGroupItemsModal, showStockGroupDetailsModal, showDealerSearchModal]);

  // Modify the checkPointsStatus function to be more robust
  const checkPointsStatus = () => {
    // Check if summary points is zero
    if (summary.totalPoints === 0) {
      console.log('Points calculation complete, stopping animation');
      setLoading(false);
      setApiRequestState('idle');
      setRequestProgress(0);
      return;
    }
    
    console.log(`Points are still being calculated (${summary.totalPoints} remaining), continuing animation...`);
    // If points are not zero, keep checking every 2 seconds
    setTimeout(checkPointsStatus, 2000);
  };

  const fetchAdminCompanies = async () => {
    if (hasTriedFetchingCompanies || loadingCompanyInfo) {
      console.log('DEBUG Frontend: Skipping fetch - already tried or loading');
      return; // Prevent multiple simultaneous calls
    }
    
    console.log('DEBUG Frontend: Starting to fetch admin companies');
    setLoadingCompanyInfo(true);
    setHasTriedFetchingCompanies(true);
    
    try {
      // Get current domain for filtering company access
      const currentDomain = window.location.hostname;
      
      console.log('DEBUG Frontend: Fetching companies with domain:', currentDomain);
      
      // Fetch companies that are assigned to admin AND match the current domain
      // The API will filter admin's assigned companies to only show domain-matching ones
      const response = await api.get('/admins/me/companies/combined', {
        params: { domain: currentDomain }
      });
      const adminCompanies = response.data;
      
      console.log('DEBUG Frontend: Received response:', adminCompanies);

      if (adminCompanies && adminCompanies.length > 0) {
        console.log('Received companies from intersection API:', adminCompanies);
        setAvailableCompanies(adminCompanies);
        setCompanies(adminCompanies); // Also set in main companies state
        // Note: Company selection will be handled by the useEffect that watches availableCompanies
      } else {
        console.warn('No companies returned from intersection API');
        showAlert('error', 'No companies available that match both your admin permissions and current access context. Please contact support.');
      }
    } catch (err) {
      console.error('Error fetching admin companies:', err);
      
      // Provide more specific error messages and fallback options
      if (err.response?.status === 404) {
        showAlert('error', 'Admin not found. Please check your account status or contact support.');
      } else if (err.response?.status === 403) {
        showAlert('error', 'Access denied. You may not have permission to access company information.');
      } else if (err.response?.status === 500) {
        showAlert('error', 'Server error occurred while fetching companies. Please try again later.');
      } else {
        showAlert('error', 'Failed to fetch available companies. Please contact support.');
      }
    } finally {
      setLoadingCompanyInfo(false);
    }
  };

  // Fix the fetchCompanies function
  const fetchCompanies = async () => {
    try {
      console.log('Fetching companies...');
      
      // Try multiple possible endpoints
      let response;
      try {
        response = await api.get('/companies');
        console.log('Primary /companies endpoint successful');
      } catch (error) {
        console.log('Primary companies endpoint failed, trying alternatives...');
        try {
          response = await api.get('/company');
          console.log('Alternative /company endpoint successful');
        } catch (secondError) {
          console.log('Both company endpoints failed, trying /api/companies...');
          response = await api.get('/api/companies');
        }
      }
      
      console.log('Companies response:', response.data);
      
      // Handle different response structures
      let companiesData = [];
      if (Array.isArray(response.data)) {
        companiesData = response.data;
      } else if (response.data.companies) {
        companiesData = response.data.companies;
      } else if (response.data.data) {
        companiesData = response.data.data;
      } else if (response.data.success && response.data.data) {
        companiesData = response.data.data;
      }
      
      console.log('Processed companies data:', companiesData);
      setCompanies(companiesData);
      
      // Get matching companies from localStorage or context
      const matchingCompaniesFromStorage = localStorage.getItem('matchingCompanies');
      let userCompanies = [];
      
      if (matchingCompaniesFromStorage) {
        try {
          const parsed = JSON.parse(matchingCompaniesFromStorage);
          if (Array.isArray(parsed) && parsed.length > 0) {
            userCompanies = parsed;
          }
        } catch (err) {
          console.error('Error parsing stored companies:', err);
        }
      }
      
      // If we have matching companies in context
      if (user?.matchingCompanies && Array.isArray(user.matchingCompanies) && user.matchingCompanies.length > 0) {
        userCompanies = user.matchingCompanies;
      }
      
      console.log('User companies:', userCompanies);
      
      // Set user's company based on various sources
      if (user && user.company) {
        // User has a single company assigned
        const companyId = typeof user.company === 'object' ? user.company._id : user.company;
        setSelectedCompany(companyId);
        console.log('Set company from user context:', companyId);
      } else if (userCompanies.length === 1) {
        // User has exactly one matching company
        setSelectedCompany(userCompanies[0]._id || userCompanies[0]);
        console.log('Set company from matching companies (single):', userCompanies[0]._id || userCompanies[0]);
      } else if (localStorage.getItem('selectedCompanyID')) {
        // User has previously selected a company
        const storedCompanyId = localStorage.getItem('selectedCompanyID');
        setSelectedCompany(storedCompanyId);
        console.log('Set company from localStorage:', storedCompanyId);
      } else if (companiesData.length > 0) {
        // Fallback to first company in the list
        setSelectedCompany(companiesData[0]._id);
        console.log('Set company to first available:', companiesData[0]._id);
      }
      
      console.log(`User has access to ${userCompanies.length} companies, total companies: ${companiesData.length}`);
    } catch (error) {
      console.error('Error fetching companies:', error);
      console.error('Error details:', error.response?.data);
      
      // Show user-friendly error
      showAlert('error', 'Failed to load companies. Please check your connection and try again.');
      
      // Set empty array to prevent undefined errors
      setCompanies([]);
    }
  };

  // Update the fetchCampaigns function to auto-start dealer performance when default campaign is selected
  const fetchCampaigns = async (companyId = selectedCompany) => {
    if (!companyId) {
      console.log('No company selected, cannot fetch campaigns');
      return;
    }
    
    try {
      console.log('Fetching campaigns for company:', companyId);
      
      // Try multiple possible endpoints
      let response;
      try {
        response = await api.get('/campaigns', {
          params: { companyID: companyId }
        });
        console.log('Primary /campaigns endpoint successful');
      } catch (error) {
        console.log('Primary campaigns endpoint failed, trying alternatives...');
        try {
          response = await api.get(`/campaigns/${companyId}`);
          console.log('Alternative campaigns endpoint successful');
        } catch (secondError) {
          console.log('Both campaign endpoints failed, trying /api/campaigns...');
          response = await api.get('/api/campaigns', {
            params: { companyID: companyId }
          });
        }
      }
      
      console.log('Campaigns response:', response.data);
      
      // Handle different response structures
      let campaignsData = [];
      if (Array.isArray(response.data)) {
        campaignsData = response.data;
      } else if (response.data.campaigns) {
        campaignsData = response.data.campaigns;
      } else if (response.data.data) {
        campaignsData = response.data.data;
      } else if (response.data.success && response.data.data) {
        campaignsData = response.data.data;
      }
      
      console.log('Processed campaigns data:', campaignsData);
      setCampaigns(campaignsData);

      // Set first active campaign as default if available
      const activeCampaign = campaignsData.find(camp =>
        camp.is_active &&
        new Date(camp.date_from) <= new Date() &&
        new Date(camp.date_to) >= new Date()
      );

      let selectedCampaignId = '';
      if (activeCampaign) {
        selectedCampaignId = activeCampaign._id;
        setSelectedCampaign(activeCampaign._id);
        console.log('Set active campaign:', activeCampaign.name);
      } else if (campaignsData.length > 0) {
        selectedCampaignId = campaignsData[0]._id;
        setSelectedCampaign(campaignsData[0]._id);
        console.log('Set first campaign:', campaignsData[0].name);
      } else {
        setSelectedCampaign('');
        console.log('No campaigns available');
        return;
      }

      // Auto-start dealer performance report when campaign is selected
      if (selectedCampaignId && companyId) {
        console.log('Auto-starting dealer performance report for selected campaign');
        // Small delay to ensure state is updated
        setTimeout(() => {
          fetchCombinedDealerData(1);
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      console.error('Error details:', error.response?.data);
      
      // Show user-friendly error
      showAlert('error', 'Failed to load campaigns. Please check your connection and try again.');
      
      // Set empty array to prevent undefined errors
      setCampaigns([]);
      setSelectedCampaign('');
    }
  };

  // Fetch stock groups separately to have them available for reference
  const fetchStockGroups = async (companyId = selectedCompany) => {
    try {
      const response = await api.get('/tally-stock-groups', {
        params: { companyID: companyId }
      });
      
      const groups = response.data || [];
      setStockGroups(groups);
      
      const parentMapping = {};
      groups.forEach(group => {
        if (group._id) {
          parentMapping[group._id.toString()] = group.Name;
        }
        if (group.id) {
          parentMapping[group.id.toString()] = group.Name;
        }
      });
      
      return parentMapping;
    } catch (error) {
      console.error('Error fetching stock groups:', error);
      return {};
    }
  };

  // Update the useEffect to properly fetch campaigns when company changes
  useEffect(() => {
    if (selectedCompany) {
      console.log('Company changed, fetching campaigns and stock groups for:', selectedCompany);
      // Clear existing campaigns first to prevent showing old data
      setCampaigns([]);
      setSelectedCampaign('');
      // Reset report data
      setDealerData([]);
      setStockGroupData([]);
      setSummary({});
      // Then fetch new data
      fetchCampaigns(selectedCompany);
      fetchStockGroups(selectedCompany);
    } else {
      console.log('No company selected, clearing campaigns');
      setCampaigns([]);
      setSelectedCampaign('');
      setDealerData([]);
      setStockGroupData([]);
      setSummary({});
    }
  }, [selectedCompany]);

  // Update the useEffect for campaign changes to auto-start dealer performance
  useEffect(() => {
    if (selectedCompany && selectedCampaign) {
      console.log('Campaign changed, auto-starting dealer performance report');
      
      // Fetch dealer performance data
      setTimeout(() => {
        if (reportType === 'dealer_performance') {
          fetchCombinedDealerData(1);
        } else if (reportType === 'stock_group_points') {
          fetchStockGroupData(1);
        }
      }, 100);
    }
  }, [selectedCampaign, selectedCompany, reportType]);

  // Fix the fetchEnhancedSummary function
  const fetchEnhancedSummary = async () => {
    if (!selectedCampaign || !selectedCompany) return;
    
    try {
      console.log('Fetching enhanced summary for:', { selectedCompany, selectedCampaign });
      
      const response = await api.get('/reports/dealer-points/enhanced-summary', {
        params: {
          period: 'campaign_duration',
          campaignId: selectedCampaign,
          companyID: selectedCompany
        }
      });
      
      console.log('Enhanced summary response:', response.data);
      
      if (response.data.success) {
        const data = response.data.data || {};
        const summaryData = {
          ...data,
          totalPoints: parseFloat(data.totalPoints) || 0,
          totalSales: parseFloat(data.totalSales) || 0,
          totalVouchers: parseInt(data.totalVouchers) || 0,
          activeDealers: parseInt(data.activeDealers) || 0,
          totalDealers: parseInt(data.totalDealers) || 0,
          totalTallyDealers: parseInt(data.totalTallyDealers) || 0
        };
        
        setSummary(summaryData);
        setApiRequestState('complete');
        setRequestProgress(100);
        
        setTimeout(() => {
          setLoading(false);
          setApiRequestState('idle');
          setRequestProgress(0);
        }, 1000);
      } else {
        throw new Error(response.data.msg || 'Failed to fetch enhanced summary');
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      showAlert('error', 'Failed to fetch enhanced summary data: ' + (error.response?.data?.msg || error.message));
      
      setTimeout(() => {
        setLoading(false);
        setApiRequestState('idle');
        setRequestProgress(0);
      }, 800);
    }
  };

  // Enhanced fetchCombinedDealerData function with improved loading coordination
  const fetchCombinedDealerData = async (page = 1) => {
    if (!selectedCampaign || !selectedCompany) {
      console.log('Missing required data:', { selectedCampaign, selectedCompany });
      return;
    }
    
    // Set loading states BEFORE starting the request
    setLoading(true);
    setLoadingTotalPoints(true);
    setApiRequestState('connecting');
    setRequestProgress(5);
    
    try {
      console.log('Fetching combined dealer data for:', { selectedCompany, selectedCampaign, page });
      
      // Progressive loading states
      setTimeout(() => {
        if (loading) {
          setApiRequestState('receiving');
          setRequestProgress(20);
        }
      }, 300);
      
      // Fetch both dealer data and enhanced summary in parallel for better performance
      const [dealerResponse, summaryResponse] = await Promise.all([
        api.get('/reports/dealer-points/combined-data', {
          params: {
            page,
            limit: 10,
            companyID: selectedCompany,
            campaignId: selectedCampaign,
            period: 'campaign_duration'
          }
        }),
        api.get('/reports/dealer-points/enhanced-summary', {
          params: {
            companyID: selectedCompany,
            campaignId: selectedCampaign,
            period: 'campaign_duration'
          }
        })
      ]);

      console.log('Combined responses:', { 
        dealerData: dealerResponse.data.success, 
        summaryData: summaryResponse.data.success 
      });

      if (dealerResponse.data.success && summaryResponse.data.success) {
        setApiRequestState('processing');
        setRequestProgress(70);
        
        const dealerData = dealerResponse.data.data || [];
        const pagination = dealerResponse.data.pagination || {};
        const summaryData = summaryResponse.data.data || {};
        
        // Set dealer data
        setDealerData(dealerData);
        setCurrentPage(pagination.page || 1);
        setTotalPages(pagination.pages || 1);
        
        // Update comprehensive summary data
        setSummary(prev => ({
          ...prev,
          totalSales: summaryData.totalSales || 0,
          totalPoints: summaryData.totalPoints || 0,
          totalVouchers: summaryData.totalVouchers || 0,
          activeDealers: summaryData.activeDealers || 0,
          totalDealers: summaryData.totalDealers || 0,
          totalTallyDealers: summaryData.totalTallyDealers || 0,
          campaignInfo: summaryData.campaignInfo || {}
        }));
        
        setApiRequestState('complete');
        setRequestProgress(100);
        
        // Ensure loading persists until all data is properly displayed
        setTimeout(() => {
          // Only stop loading if we have both dealer data and summary data
          if (dealerData.length >= 0 && summaryData.totalDealers >= 0) {
            setLoading(false);
            setLoadingTotalPoints(false);
            setApiRequestState('idle');
            setRequestProgress(0);
          }
        }, 800);
      } else {
        throw new Error('Failed to fetch complete data');
      }
    } catch (error) {
      console.error('Error fetching combined dealer data:', error);
      showAlert('error', 'Failed to fetch dealer data: ' + (error.response?.data?.msg || error.message));
      setDealerData([]);
      setSummary({});
      
      // Reset loading states on error
      setLoading(false);
      setLoadingTotalPoints(false);
      setApiRequestState('idle');
      setRequestProgress(0);
    }
  };

  // Add loading state management for stock group pagination
  const fetchStockGroupData = async (page = 1) => {
    if (!selectedCampaign || !selectedCompany) {
      console.log('Missing campaign or company for stock group data');
      return;
    }

    setLoadingStockGroups(true);
    setError('');
    setApiRequestState('connecting');
    setRequestProgress(10);

    try {
      setApiRequestState('receiving');
      setRequestProgress(50);

      console.log(`Fetching stock group data for page ${page}`);

      const response = await api.get('/reports/dealer-points/stock-group-points', {
        params: {
          companyID: selectedCompany,
          campaignId: selectedCampaign,
          page: page,
          limit: stockGroupLimit
        }
      });

      setApiRequestState('processing');
      setRequestProgress(80);

      if (response.data.success) {
        setStockGroupData(response.data.data);
        
        // Store stock group columns for dynamic table headers
        setStockGroupColumns(response.data.stockGroupColumns || []);
        
        // Enhanced pagination info with summary
        const paginationInfo = {
          ...response.data.pagination,
          summary: response.data.summary
        };
        setStockGroupPagination(paginationInfo);
        setStockGroupPage(page);
        
        setApiRequestState('complete');
        setRequestProgress(100);
        
        console.log(`Stock group data loaded successfully: ${response.data.data.length} dealers on page ${page}`);
        console.log('Stock group columns:', response.data.stockGroupColumns);
        console.log('Pagination info:', paginationInfo);
        console.log('Summary:', response.data.summary);
      } else {
        throw new Error(response.data.msg || 'Failed to fetch stock group data');
      }
    } catch (error) {
      console.error('Error fetching stock group data:', error);
      setApiRequestState('error');
      
      if (error.response?.status === 404) {
        showAlert('info', 'No dealers found with points from stock group rules in this campaign');
        setStockGroupData([]);
        setStockGroupColumns([]);
        setStockGroupPagination({});
      } else {
        showAlert('error', `Failed to fetch stock group data: ${error.message}`);
      }
    } finally {
      setLoadingStockGroups(false);
      setTimeout(() => {
        setApiRequestState('idle');
        setRequestProgress(0);
      }, 1000);
    }
  };

  // View dealers benefiting from a specific stock group
  const handleViewStockGroupDealers = async (stockGroup) => {
    if (!stockGroup.stockGroupId || !selectedCampaign) return;
    
    setLoadingDetails(true);
    
    try {
      const response = await api.get(`/reports/dealer-points/stock-group-dealers/${stockGroup.stockGroupId}`, {
        params: {
          campaignId: selectedCampaign,
          companyID: selectedCompany,
          period: 'campaign_duration'
        }
      });
      
      if (response.data.success) {
        const dealersList = response.data.dealers || [];
        
        // Create a detailed view of dealers for this stock group
        const modalContent = {
          stockGroup: stockGroup,
          dealers: dealersList,
          totalDealers: dealersList.length,
          totalPoints: dealersList.reduce((sum, d) => sum + (d.points || 0), 0),
          totalSales: dealersList.reduce((sum, d) => sum + (d.sales || 0), 0)
        };
        
        setSelectedStockGroupDetails(modalContent);
        setShowStockGroupDealersModal(true);
      }
    } catch (error) {
      console.error('Error fetching stock group dealers:', error);
      showAlert('error', 'Failed to fetch dealers for this stock group');
    } finally {
      setLoadingDetails(false);
    }
  };

  // View items sold under a specific stock group
  const handleViewStockGroupItems = async (stockGroup) => {
    if (!stockGroup.stockGroupId || !selectedCampaign) return;
    
    setLoadingDetails(true);
    
    try {
      const response = await api.get(`/reports/dealer-points/stock-group-items/${stockGroup.stockGroupId}`, {
        params: {
          campaignId: selectedCampaign,
          companyID: selectedCompany,
          period: 'campaign_duration'
        }
      });
      
      if (response.data.success) {
        const itemsList = response.data.items || [];
        
        // Create a detailed view of items for this stock group
        const modalContent = {
          stockGroup: stockGroup,
          items: itemsList,
          totalItems: itemsList.length,
          totalQuantity: itemsList.reduce((sum, i) => sum + (i.quantity || 0), 0),
          totalValue: itemsList.reduce((sum, i) => sum + (i.amount || 0), 0)
        };
        
        setSelectedStockGroupItems(modalContent);
        setShowStockGroupItemsModal(true);
      }
    } catch (error) {
      console.error('Error fetching stock group items:', error);
      showAlert('error', 'Failed to fetch items for this stock group');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Fix Issue 2: Include inactive dealers in dealer performance export
  const handleExportAllDealerPerformance = async () => {
    if (!selectedCompany || !selectedCampaign) {
      showAlert('error', 'Please select a company and campaign first');
      return;
    }

    setExporting(true);
    try {
      // Fetch ALL dealer data (including inactive dealers)
      const allDealersResponse = await api.get('/reports/dealer-points/combined-data', {
        params: {
          companyID: selectedCompany,
          campaignId: selectedCampaign,
          period: 'campaign_duration',
          includeInactive: true, // Add this parameter
          page: 1,
          limit: 10000
        }
      });

      if (!allDealersResponse.data.success) {
        throw new Error(allDealersResponse.data.msg || 'Failed to fetch dealer data');
      }

      const allDealers = allDealersResponse.data.data || [];
      
      if (allDealers.length === 0) {
        showAlert('warning', 'No dealer data found to export');
        return;
      }

      // Prepare CSV data with dealer status separation
      const csvHeader = [
        'Dealer Name',
        'Dealer ID',
        'Status',
        'Total Sales',
        'Total Points',
        'Voucher Count',
        'Avg per Voucher',
        'Phone',
        'Owner',
        'Address',
        'GST Number'
      ];

      const csvData = allDealers.map(dealer => [
        safeString(dealer.dealerName),
        safeString(dealer.dealerUniqueId),
        // Fix: Include inactive dealers but mark them clearly
        (dealer.totalPoints > 0) ? 'Active' : 'Inactive',
        safeNumber(dealer.totalSales || 0),
        safeNumber(dealer.totalPoints || 0),
        safeNumber(dealer.voucherCount || 0),
        dealer.voucherCount > 0 ? (dealer.totalSales / dealer.voucherCount).toFixed(2) : '0',
        safeString(dealer.dealerPhone),
        safeString(dealer.dealerOwner),
        safeString(dealer.dealerAddress),
        safeString(dealer.dealerGstin)
      ]);

      // Sort data: Active dealers first, then inactive
      csvData.sort((a, b) => {
        const aIsActive = a[2] === 'Active';
        const bIsActive = b[2] === 'Active';
        if (aIsActive && !bIsActive) return -1;
        if (!aIsActive && bIsActive) return 1;
        return 0;
      });

      // Create CSV content
      const csvContent = [csvHeader, ...csvData]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `dealer_performance_all_${selectedCampaign}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const activeCount = csvData.filter(row => row[2] === 'Active').length;
      const inactiveCount = csvData.filter(row => row[2] === 'Inactive').length;
      
      showAlert('success', `Successfully exported ${allDealers.length} dealer records (${activeCount} active, ${inactiveCount} inactive)`);

    } catch (error) {
      console.error('Export error:', error);
      showAlert('error', `Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Fix the stock group export function to use correct data mapping
  const handleExportAllStockGroupPoints = async () => {
    if (!selectedCompany || !selectedCampaign) {
      showAlert('error', 'Please select a company and campaign first');
      return;
    }

    setExporting(true);
    try {
      // Fetch all stock group data
      const allStockGroupsResponse = await api.get('/reports/dealer-points/stock-group-points', {
        params: {
          companyID: selectedCompany,
          campaignId: selectedCampaign,
          page: 1,
          limit: 10000
        }
      });

      if (!allStockGroupsResponse.data.success) {
        throw new Error(allStockGroupsResponse.data.msg || 'Failed to fetch stock group data');
      }

      const allStockGroupDealers = allStockGroupsResponse.data.data || [];
      const stockGroupColumns = allStockGroupsResponse.data.stockGroupColumns || [];
      
      if (allStockGroupDealers.length === 0) {
        showAlert('warning', 'No stock group data found to export');
        return;
      }

      // Prepare dynamic CSV header based on stock group columns
      const csvHeader = [
        'Dealer Name',
        'Dealer ID',
        'Status',
        'Total Sales from Rules',
        'Total Points from Rules',
        'Total Vouchers from Rules',
        'Total Sales (All Activity)',
        'Total Vouchers (All Activity)',
        ...stockGroupColumns.map(col => `${col.stockGroupName} Points`),
        ...stockGroupColumns.map(col => `${col.stockGroupName} Sales`)
      ];

      const csvData = allStockGroupDealers.map(dealer => {
        // Fix: Use the correct stockGroups array structure from backend
        const stockGroupPoints = stockGroupColumns.map(col => {
          if (dealer.stockGroups && Array.isArray(dealer.stockGroups)) {
            const stockGroupData = dealer.stockGroups.find(sg => 
              sg.stockGroupId === col.stockGroupId
            );
            return safeNumber(stockGroupData?.points || 0);
          }
          return 0;
        });
        
        const stockGroupSales = stockGroupColumns.map(col => {
          if (dealer.stockGroups && Array.isArray(dealer.stockGroups)) {
            const stockGroupData = dealer.stockGroups.find(sg => 
              sg.stockGroupId === col.stockGroupId
            );
            return safeNumber(stockGroupData?.sales || 0);
          }
          return 0;
        });

        return [
          safeString(dealer.dealerName),
          safeString(dealer.dealerUniqueId),
          (dealer.totalPointsFromRules > 0 || dealer.totalSalesFromRules > 0) ? 'Active' : 'Inactive',
          safeNumber(dealer.totalSalesFromRules || 0),
          safeNumber(dealer.totalPointsFromRules || 0),
          safeNumber(dealer.totalVouchersFromRules || 0),
          safeNumber(dealer.totalSalesAllActivity || 0),
          safeNumber(dealer.totalVouchersAllActivity || 0),
          ...stockGroupPoints,
          ...stockGroupSales
        ];
      });

      // Sort data: Active dealers first, then inactive
      csvData.sort((a, b) => {
        const aIsActive = a[2] === 'Active';
        const bIsActive = b[2] === 'Active';
        if (aIsActive && !bIsActive) return -1;
        if (!aIsActive && bIsActive) return 1;
        return 0;
      });

      // Create CSV content
      const csvContent = [csvHeader, ...csvData]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `stock_group_points_all_${selectedCampaign}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const activeCount = csvData.filter(row => row[2] === 'Active').length;
      const inactiveCount = csvData.filter(row => row[2] === 'Inactive').length;

      showAlert('success', `Successfully exported ${allStockGroupDealers.length} dealer records (${activeCount} active, ${inactiveCount} inactive)`);

    } catch (error) {
      console.error('Export error:', error);
      showAlert('error', `Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Handle company selection change
  const handleCompanyChange = (e) => {
    const newCompanyId = e.target.value;
    
    console.log('Company selection changed from', selectedCompany, 'to', newCompanyId);
    
    // Store selected company in localStorage for persistence
    if (newCompanyId) {
      localStorage.setItem('selectedCompanyID', newCompanyId);
    } else {
      localStorage.removeItem('selectedCompanyID');
    }
    
    // Set the new company first - this will trigger the useEffect which handles clearing and fetching
    setSelectedCompany(newCompanyId);
    
    // Also immediately clear campaigns to prevent flash of old data
    setCampaigns([]);
    setSelectedCampaign('');
  };

  // Add a helper function to reset pagination when switching report types
  const resetPaginationStates = () => {
    setCurrentPage(1);
    setStockGroupPage(1);
    setTotalPages(1);
    setStockGroupPagination({});
  };

  // Update the loading message to show meaningful status based on statistics
  const getLoadingMessage = () => {
    if (apiRequestState === 'connecting') return 'Establishing connection to server...';
    if (apiRequestState === 'receiving') return 'Downloading dealer records...';
    if (apiRequestState === 'processing') {
      if (summary.totalPoints > 0) {
        return `Processing campaign points... (${formatPoints(summary.totalPoints)} points remaining)`;
      }
      if (summary.totalDealers > 0 && summary.activeDealers === 0) {
        return 'Processing dealer statistics...';
      }
      return 'Processing campaign data...';
    }
    if (apiRequestState === 'complete') return 'Finalizing data...';
    if (summary.totalPoints > 0) {
      return `Calculating points... (${formatPoints(summary.totalPoints)} points remaining)`;
    }
    return 'Fetching dealer records...';
  };

  // Handle campaign selection change
  const handleCampaignChange = (e) => {
    const newCampaignId = e.target.value;
    setSelectedCampaign(newCampaignId);
    
    // Reset dependent states
    setDealerData([]);
    setStockGroupData([]);
    setStockGroupColumns([]);
    setSummary({});
    setSelectedDealerId(null);
    setDealerDetails(null);
    
    // Reset pagination states
    resetPaginationStates();
    
    // Fetch data for the new campaign if selected
    if (newCampaignId && selectedCompany) {
      if (reportType === 'dealer_performance') {
        fetchCombinedDealerData(1);
      } else if (reportType === 'stock_group_points') {
        fetchStockGroupData(1);
      }
    }
  };

    const handleReportTypeChange = (type) => {
    setReportType(type);
    setCurrentPage(1);
    setSelectedDealerId(null);
    setDealerDetails(null);
    setSummary({});
    
    // Fetch appropriate data based on report type
    if (type === 'dealer_performance') {
      fetchCombinedDealerData(1);
    } else if (type === 'stock_group_points') {
      fetchStockGroupData(1);
    }
  };

  // Update the handleDealerClick function to use the unified modal
  const handleDealerClick = async (dealerId) => {
    if (!dealerId || !selectedCampaign) {
      showAlert('warning', 'Please select a dealer and campaign');
      return;
    }
    
    setSelectedDealerId(dealerId);
    setLoadingDetails(true);
    setApiRequestState('connecting');
    setRequestProgress(10);
    
    try {
      setApiRequestState('receiving');
      setRequestProgress(50);
      
      const response = await api.get(`/reports/dealer-points/dealer-details/${dealerId}`, {
        params: {
          companyID: selectedCompany,
          campaignId: selectedCampaign
        }
      });
      
      setApiRequestState('processing');
      setRequestProgress(80);
      
      if (response.data && response.data.success) {
        const dealerData = response.data;
        
        console.log('Dealer details received:', dealerData);
        console.log('Vouchers in response:', dealerData.vouchers);
        
        // Ensure dealerData has the vouchers property
        if (!dealerData.vouchers) {
          console.warn('No vouchers found in dealer data, setting empty array');
          dealerData.vouchers = [];
        }
        
        setDealerDetails(dealerData);
        setActiveVouchers(dealerData.vouchers || []);
        setUnifiedModalView('details'); 
        setShowUnifiedDealerModal(true);
        
        setApiRequestState('complete');
        setRequestProgress(100);
        
        console.log('Dealer details loaded successfully');
      } else {
        throw new Error(response.data?.msg || 'Failed to fetch dealer details');
      }
    } catch (error) {
      console.error('Error fetching dealer details:', error);
      setApiRequestState('error');
      
      if (error.response?.status === 404) {
        showAlert('warning', 'Dealer details not found for the selected campaign period');
      } else {
        showAlert('error', `Failed to fetch dealer details: ${error.message}`);
      }
    } finally {
      setLoadingDetails(false);
      setTimeout(() => {
        setApiRequestState('idle');
        setRequestProgress(0);
      }, 1000);
    }
  };

  // Functions for specific dealer search modal
  const handleOpenDealerSearchModal = () => {
    setShowDealerSearchModal(true);
    setDealerSearchQuery('');
    setSearchResults([]);
    setSelectedSearchDealer(null);
  };

  const handleCloseDealerSearchModal = () => {
    setShowDealerSearchModal(false);
    setDealerSearchQuery('');
    setSearchResults([]);
    setSelectedSearchDealer(null);
    setLoadingSearch(false);
  };

  const handleDealerSearch = async (query) => {
    setDealerSearchQuery(query);
    
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    if (!selectedCompany || !selectedCampaign) {
      showAlert('warning', 'Please select a company and campaign first');
      return;
    }

    setLoadingSearch(true);
    
    try {
      console.log('Searching for dealers with query:', query);
      
      const response = await api.get('/reports/dealer-points/search-dealers', {
        params: {
          companyID: selectedCompany,
          campaignId: selectedCampaign,
          query: query,
          limit: 50
        }
      });

      if (response.data.success) {
        setSearchResults(response.data.dealers || []);
        console.log(`Found ${response.data.dealers?.length || 0} dealers matching "${query}"`);
      } else {
        setSearchResults([]);
        showAlert('error', response.data.msg || 'Search failed');
      }
    } catch (error) {
      console.error('Error searching dealers:', error);
      setSearchResults([]);
      showAlert('error', 'Failed to search dealers: ' + (error.response?.data?.msg || error.message));
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSelectDealerFromSearch = async (dealer) => {
    setSelectedSearchDealer(dealer);
    setShowDealerSearchModal(false);
    
    // Open dealer details using the same logic as existing handleDealerClick
    await handleDealerClick(dealer.dealerId);
  };

  // Fetch dealer ledger associations
  const fetchDealerLedgers = async (dealerId) => {
    if (!dealerId) return;
    
    setLoadingDetails(true);
    
    try {
      const response = await api.get(`/reports/dealer-points/dealer-ledgers/${dealerId}`, {
        params: {
          companyID: selectedCompany,
          includeAssociatedDealers: true
        }
      });

      if (response.data.success) {
        setActiveLedgers(response.data.ledgers || []);
        setAssociatedDealers(response.data.associatedDealers || []);
      } else {
        showAlert('error', 'Failed to fetch ledger associations');
      }
    } catch (error) {
      console.error('Error fetching dealer ledgers:', error);
      showAlert('error', 'Failed to fetch ledger associations');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Enhanced voucher fetching with better pagination and error handling
  const fetchAllDealerVouchersEnhanced = async (dealerId) => {
    if (!dealerId) {
      showAlert('warning', 'Invalid dealer ID');
      return;
    }
    
    if (!selectedCampaign) {
      showAlert('warning', 'Please select a campaign first');
      return;
    }
    
    setLoadingDetails(true);
    setApiRequestState('connecting');
    setRequestProgress(5);
    
    try {
      const campaignObj = campaigns.find(c => c._id === selectedCampaign) || {};
      const campaignDates = campaignObj ? {
        startDate: campaignObj.date_from,
        endDate: campaignObj.date_to
      } : {};
      
      setApiRequestState('receiving');
      setRequestProgress(20);
      
      const response = await api.get(`/reports/dealer-points/dealer-vouchers/${dealerId}`, {
        params: {
          period: 'campaign_duration',
          campaignId: selectedCampaign,
          companyID: selectedCompany,
          includeItems: true,
          includePointsBreakdown: true,
          limit: 1000, // Get all vouchers for the campaign period
          ...campaignDates
        }
      });

      setApiRequestState('processing');
      setRequestProgress(80);

      if (response.data.success) {
        const vouchers = response.data.vouchers || [];
        setActiveVouchers(vouchers);
        
        setApiRequestState('complete');
        setRequestProgress(100);
        
        showAlert('success', `Found ${vouchers.length} vouchers for this dealer in the campaign period`);
        
        setTimeout(() => {
          setApiRequestState('idle');
          setRequestProgress(0);
        }, 1000);
      } else {
        throw new Error(response.data.message || 'No vouchers found');
      }
    } catch (error) {
      console.error('Error fetching dealer vouchers:', error);
      
      if (error.response?.status === 404) {
        showAlert('info', 'No vouchers found for this dealer in the selected campaign period');
        setActiveVouchers([]);
      } else if (error.response?.status === 403) {
        showAlert('warning', 'You do not have permission to view this dealer\'s vouchers');
      } else {
        showAlert('error', error.response?.data?.message || 'Failed to fetch dealer vouchers');
      }
      
      setApiRequestState('idle');
      setRequestProgress(0);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Update the fetchAllDealerVouchers to use the enhanced version
  const fetchAllDealerVouchers = fetchAllDealerVouchersEnhanced;

  // Navigation functions for unified modal
  const goToDetails = () => {
    setUnifiedModalView('details');
  };

  const goToVouchers = () => {
    setUnifiedModalView('vouchers');
  };

  const goBackFromPointBreakdown = () => {
    setUnifiedModalView('vouchers');
    setSelectedVoucher(null);
  };

  const closeUnifiedModal = () => {
    setShowUnifiedDealerModal(false);
    setUnifiedModalView('details');
    setSelectedVoucher(null);
    setDealerDetails(null);
    setSelectedDealerId(null);
    setDealerPointsDetails(null);
    setLoadingPointsDetails(false);
  };

  // Handle viewing points structure for a voucher
  const handleViewPointsStructure = (voucher) => {
    setSelectedVoucher(voucher);
    setUnifiedModalView('pointBreakdown');
  };

  // Handle viewing comprehensive points details for a dealer
  const handleViewPointsDetails = async () => {
    if (!selectedDealerId || !selectedCampaign) {
      showAlert('warning', 'Missing dealer or campaign information');
      return;
    }

    setLoadingPointsDetails(true);
    
    try {
      const response = await api.get(`/reports/dealer-points/dealer-points-details/${selectedDealerId}`, {
        params: {
          companyID: selectedCompany,
          campaignId: selectedCampaign,
          includeVoucherDetails: 'true'
        }
      });

      if (response.data && response.data.success) {
        setDealerPointsDetails(response.data);
        setUnifiedModalView('pointsDetails');
        showAlert('success', 'Points details loaded successfully');
      } else {
        throw new Error(response.data?.msg || 'Failed to fetch points details');
      }
    } catch (error) {
      console.error('Error fetching dealer points details:', error);
      showAlert('error', `Failed to fetch points details: ${error.message}`);
    } finally {
      setLoadingPointsDetails(false);
    }
  };

  const goToPointsDetails = () => {
    handleViewPointsDetails();
  };

  const goBackFromPointsDetails = () => {
    setUnifiedModalView('details');
    setDealerPointsDetails(null);
  };

  // Handle export all vouchers
  const handleExportAllVouchers = async () => {
    if (!dealerDetails?.vouchers || dealerDetails.vouchers.length === 0) {
      showAlert('warning', 'No vouchers to export');
      return;
    }

    setExportingDetails(true);
    
    try {
      // Create CSV content
      const headers = ['Voucher Number', 'Date', 'Amount', 'Points', 'Items Count'];
      const csvContent = [
        headers.join(','),
        ...dealerDetails.vouchers.map(voucher => [
          `"${safeString(voucher.voucherNumber)}"`,
          `"${formatDate(voucher.date)}"`,
          safeNumber(voucher.totalAmount),
          safeNumber(voucher.totalPoints),
          voucher.items ? voucher.items.length : 0
        ].join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dealerDetails.dealer?.businessName || 'dealer'}_vouchers.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showAlert('success', 'Vouchers exported successfully');
    } catch (error) {
      console.error('Error exporting vouchers:', error);
      showAlert('error', 'Failed to export vouchers');
    } finally {
      setExportingDetails(false);
    }
  };

  // Update the getCompaniesForSelection function to handle domain-based filtering for Admin users
  const getCompaniesForSelection = () => {
    console.log('Getting companies for selection, total companies:', companies.length);
    
    // For Admin users, prioritize availableCompanies from domain-based filtering
    if (user?.role === 'Admin' && availableCompanies.length > 0) {
      console.log('Returning domain-filtered companies for Admin:', availableCompanies.length);
      return availableCompanies;
    }
    
    if (!companies || companies.length === 0) {
      console.log('No companies available');
      return [];
    }
    
    // Get matching companies from localStorage or context
    const matchingCompaniesFromStorage = localStorage.getItem('matchingCompanies');
    let userCompanies = [];
    
    if (matchingCompaniesFromStorage) {
      try {
        const parsed = JSON.parse(matchingCompaniesFromStorage);
        if (Array.isArray(parsed) && parsed.length > 0) {
          userCompanies = parsed;
        }
      } catch (err) {
        console.error('Error parsing stored companies:', err);
      }
    }
    
    // If we have matching companies in context
    if (user?.matchingCompanies && Array.isArray(user.matchingCompanies) && user.matchingCompanies.length > 0) {
      userCompanies = user.matchingCompanies;
    }
    
    console.log('User matching companies:', userCompanies);
    
    // If user has matching companies, filter the companies list
    if (userCompanies.length > 0) {
      const userCompanyIds = userCompanies.map(c => c._id || c);
      const filteredCompanies = companies.filter(company => userCompanyIds.includes(company._id));
      console.log('Filtered companies:', filteredCompanies);
      return filteredCompanies;
    }
    
    // If user has a single assigned company, return only that
    if (user && user.company) {
      const userCompanyId = typeof user.company === 'object' ? user.company._id : user.company;
      const userCompanies = companies.filter(company => company._id === userCompanyId);
      console.log('User assigned company:', userCompanies);
      return userCompanies;
    }
    
    // Return all companies (fallback for other admin scenarios)
    console.log('Returning all companies (fallback access)');
    return companies;
  };

  // Render summary cards (only for dealer performance)
  const renderSummaryCards = () => {
    // Only show summary cards for dealer performance report
    if (reportType !== 'dealer_performance') {
      return null;
    }

    if (!summary || Object.keys(summary).length === 0) {
      return null;
    }

    return (
      <div className="space-y-6">
        {/* Progress Bar for Dealer Performance Loading */}
        {renderProgressBar(loading || loadingTotalPoints, requestProgress, 'Loading dealer performance data...')}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Sales Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Sales</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatMoney(safeNumber(summary.totalSales))}
              </p>
            </div>
          </div>
        </div>

        {/* Total Points Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.950.690h4.915c.969 0 1.371 1.240.588 1.810l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.570-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.570-.380-1.810.588-1.810h4.914a1 1 0 00.951-.690l1.519-4.674z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Points</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loadingTotalPoints ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600 mr-2"></div>
                    <span className="text-purple-600">Calculating...</span>
                  </div>
                ) : (
                  formatPoints(safeNumber(summary.totalPoints))
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Active Dealers Card - Fixed SVG path */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 01 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 01 9.288 0M15 7a3 3 0 11-6 0 3 3 0 01 6 0zm6 3a2 2 0 11-4 0 2 2 0 01 4 0zM7 10a2 2 0 11-4 0 2 2 0 01 4 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Dealers</p>
              <p className="text-2xl font-semibold text-gray-900">
                {safeString(summary.activeDealers || 0)}
              </p>
              <p className="text-xs text-gray-500">
                of {safeString(summary.totalTallyDealers || 0)} total
              </p>
            </div>
          </div>
        </div>

        {/* Total Vouchers Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Vouchers</p>
              <p className="text-2xl font-semibold text-gray-900">
                {safeString(summary.totalVouchers || 0)}
              </p>
              <p className="text-xs text-gray-500">
                Avg: {summary.totalVouchers > 0 ? 
                  formatMoney(safeNumber(summary.totalSales) / safeNumber(summary.totalVouchers)) : 
                  '₹0'} per voucher
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    );
  };

  // Render dealer performance table
  const renderDealerTable = () => {
    return (
      <div className="space-y-6">
        {/* Main Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Dealer Performance</h3>
            <div className="text-sm text-gray-500">
              {dealerData.length} dealers with activity
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dealer Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Sales
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vouchers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg. per Voucher
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dealerData && dealerData.length > 0 ? (
                  dealerData.map((dealer, index) => (
                    <tr 
                      key={dealer.dealerId || index} 
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition-colors`}
                      onClick={() => handleDealerClick(dealer.dealerId)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {safeString(dealer.dealerName)}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {safeString(dealer.dealerUniqueId)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {safeString(dealer.dealerPhone) || '–––'}
                        </div>
                        {dealer.dealerOwner && (
                          <div className="text-xs text-gray-500">
                            {safeString(dealer.dealerOwner)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatMoney(safeNumber(dealer.totalSales))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-purple-600">
                        {formatPoints(safeNumber(dealer.totalPoints))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {safeString(dealer.voucherCount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {dealer.voucherCount > 0 ? 
                          formatMoney(safeNumber(dealer.totalSales) / safeNumber(dealer.voucherCount)) : 
                          '₹0'
                        }
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 01 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 01 9.288 0M15 7a3 3 0 11-6 0 3 3 0 01 6 0zm6 3a2 2 0 11-4 0 2 2 0 01 4 0zM7 10a2 2 0 11-4 0 2 2 0 01 4 0z" />
                        </svg>
                        <p className="text-lg font-medium text-gray-900 mb-1">No dealer data available</p>
                        <p className="text-sm text-gray-500">
                          {selectedCampaign ? 
                            'No dealers have activity for this campaign period.' :
                            'Please select a campaign to view dealer performance.'
                          }
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {renderModernPagination(currentPage, totalPages, handlePageChange, loading)}
        </div>
      </div>
    );
  };

  const renderStockGroupPointsReport = () => {
    if (loadingStockGroups) {
      return (
        <div className="space-y-4">
          {renderProgressBar(true, requestProgress, getLoadingMessage())}
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-16 rounded"></div>
            ))}
          </div>
        </div>
      );
    }

    if (!stockGroupData || stockGroupData.length === 0) {
      return (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Stock Group Data</h3>
          <p className="text-gray-500">No stock group points data available for the selected campaign.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary Cards - Responsive */}
        {stockGroupPagination.summary && (
          <div className="mb-8">
            <div className="mb-4">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Stock Group Summary</h2>
              <p className="text-sm text-gray-600">Overview of stock group performance metrics</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-xl shadow-xl p-3 md:p-6 text-white transform hover:scale-105 transition-transform duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2 md:mb-3">
                      <div className="w-8 h-8 md:w-12 md:h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <svg className="w-4 h-4 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 01 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 01 9.288 0M15 7a3 3 0 11-6 0 3 3 0 01 6 0zm6 3a2 2 0 11-4 0 2 2 0 01 4 0zM7 10a2 2 0 11-4 0 2 2 0 01 4 0z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs md:text-sm font-medium text-blue-100 uppercase tracking-wide">Total Dealers</p>
                    <p className="text-lg md:text-3xl font-bold text-white mt-1">
                      {stockGroupPagination.summary.totalDealersWithActivity || 0}
                    </p>
                    <p className="text-xs text-blue-200 mt-1 md:mt-2">Active in campaign</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 rounded-xl shadow-xl p-3 md:p-6 text-white transform hover:scale-105 transition-transform duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2 md:mb-3">
                      <div className="w-8 h-8 md:w-12 md:h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <svg className="w-4 h-4 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs md:text-sm font-medium text-emerald-100 uppercase tracking-wide">Active Dealers</p>
                    <p className="text-lg md:text-3xl font-bold text-white mt-1">
                      {stockGroupPagination.summary.totalDealersWithStockGroupPoints || 0}
                    </p>
                    <p className="text-xs text-emerald-200 mt-1 md:mt-2">With stock group points</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 rounded-xl shadow-xl p-3 md:p-6 text-white transform hover:scale-105 transition-transform duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2 md:mb-3">
                      <div className="w-8 h-8 md:w-12 md:h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <svg className="w-4 h-4 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.950.690h4.915c.969 0 1.371 1.240.588 1.810l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.570-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.570-.380-1.810.588-1.810h4.914a1 1 0 00.951-.690l1.519-4.674z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs md:text-sm font-medium text-purple-100 uppercase tracking-wide">Total Points</p>
                    <p className="text-lg md:text-3xl font-bold text-white mt-1">
                      {formatPoints(stockGroupPagination.summary.totalPointsFromRules || 0)}
                    </p>
                    <p className="text-xs text-purple-200 mt-1 md:mt-2">From stock group rules</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 rounded-xl shadow-xl p-3 md:p-6 text-white transform hover:scale-105 transition-transform duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2 md:mb-3">
                      <div className="w-8 h-8 md:w-12 md:h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <svg className="w-4 h-4 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs md:text-sm font-medium text-orange-100 uppercase tracking-wide">Total Sales</p>
                    <p className="text-lg md:text-3xl font-bold text-white mt-1">
                      {formatMoney(stockGroupPagination.summary.totalSalesFromRules || 0)}
                    </p>
                    <p className="text-xs text-orange-200 mt-1 md:mt-2">From stock group rules</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Card-Based Layout for All Screen Sizes */}
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 md:px-6 py-4 rounded-lg border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">Stock Group Points Report</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Showing {stockGroupPagination.total || 0} dealers with stock group activity
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Stock Group Analysis
                </span>
                {stockGroupColumns.length > 0 && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {stockGroupColumns.length} Stock Groups
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Dealer Cards */}
          <div className="space-y-4">
            {stockGroupData.map((dealer, index) => (
              <div key={dealer.dealerId || index} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                
                {/* Card Header */}
                <div className="px-4 md:px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-semibold text-gray-900 truncate">
                        {safeString(dealer.dealerName)}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        ID: {safeString(dealer.dealerUniqueId)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {safeNumber(dealer.totalVouchersFromRules)} rule vouchers
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {safeNumber(dealer.totalVouchersAllActivity)} total vouchers
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="px-4 md:px-6 py-4 space-y-6">
                  
                  {/* Summary Stats Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Sales from Rules</p>
                          <p className="text-lg lg:text-xl font-bold text-blue-900 mt-1">
                            {formatMoney(safeNumber(dealer.totalSalesFromRules))}
                          </p>
                        </div>
                        <div className="w-8 h-8 bg-blue-200 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">Points from Rules</p>
                          <p className="text-lg lg:text-xl font-bold text-purple-900 mt-1">
                            {formatPoints(safeNumber(dealer.totalPointsFromRules))}
                          </p>
                        </div>
                        <div className="w-8 h-8 bg-purple-200 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.950.690h4.915c.969 0 1.371 1.240.588 1.810l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.570-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.570-.380-1.810.588-1.810h4.914a1 1 0 00.951-.690l1.519-4.674z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-green-700 uppercase tracking-wide">Total Sales</p>
                          <p className="text-lg lg:text-xl font-bold text-green-900 mt-1">
                            {formatMoney(safeNumber(dealer.totalSalesAllActivity))}
                          </p>
                        </div>
                        <div className="w-8 h-8 bg-green-200 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-orange-700 uppercase tracking-wide">Stock Groups</p>
                          <p className="text-lg lg:text-xl font-bold text-orange-900 mt-1">
                            {dealer.stockGroups ? dealer.stockGroups.length : 0}
                          </p>
                        </div>
                        <div className="w-8 h-8 bg-orange-200 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stock Groups Breakdown */}
                  {dealer.stockGroups && dealer.stockGroups.length > 0 && (
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Stock Group Performance
                      </h5>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {dealer.stockGroups.map((sg, sgIndex) => {
                          const column = stockGroupColumns.find(col => col.stockGroupId === sg.stockGroupId);
                          return (
                            <div key={sgIndex} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-150">
                              <div className="space-y-3">
                                <div className="flex justify-between items-start">
                                  <h6 className="text-sm font-medium text-gray-900 truncate pr-2" title={column ? column.stockGroupName : `Group ${sg.stockGroupId}`}>
                                    {column ? column.stockGroupName : `Group ${sg.stockGroupId}`}
                                  </h6>
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
                                    {formatPoints(safeNumber(sg.points))} pts
                                  </span>
                                </div>
                                
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">Sales:</span>
                                    <span className="font-medium text-gray-900">
                                      {formatMoney(safeNumber(sg.sales))}
                                    </span>
                                  </div>
                                  
                                  {sg.vouchers > 0 && (
                                    <div className="flex justify-between items-center text-sm">
                                      <span className="text-gray-600">Vouchers:</span>
                                      <span className="font-medium text-gray-900">
                                        {sg.vouchers}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {sg.sales > 0 && sg.vouchers > 0 && (
                                    <div className="flex justify-between items-center text-sm">
                                      <span className="text-gray-600">Avg/Voucher:</span>
                                      <span className="font-medium text-gray-900">
                                        {formatMoney(sg.sales / sg.vouchers)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Progress bar for relative performance */}
                                {stockGroupPagination.summary && stockGroupPagination.summary.totalSalesFromRules > 0 && (
                                  <div className="space-y-1">
                                    <div className="flex justify-between items-center text-xs text-gray-500">
                                      <span>% of Rule Sales</span>
                                      <span>{((sg.sales / stockGroupPagination.summary.totalSalesFromRules) * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                                        style={{ 
                                          width: `${Math.min(100, (sg.sales / stockGroupPagination.summary.totalSalesFromRules) * 100)}%` 
                                        }}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Empty state for dealers with no stock group activity */}
                  {(!dealer.stockGroups || dealer.stockGroups.length === 0) && (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <p className="text-sm font-medium text-gray-500">No Stock Group Activity</p>
                      <p className="text-xs text-gray-400 mt-1">This dealer has no sales from stock groups with rules</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {renderModernPagination(
            stockGroupPage,
            stockGroupPagination.pages || 1,
            handleStockGroupPageChange,
            loadingStockGroups
          )}
        </div>


      </div>
    );
  };

  const renderModals = () => {
    return (
      <>
        {/* Unified Dealer Modal - Single Modal with Multiple Views */}
        {showUnifiedDealerModal && dealerDetails && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="flex items-center justify-center min-h-full p-4">
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  {/* Modal Header with Navigation */}
                  <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-4 border-b">
                    <div className="flex items-center space-x-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {unifiedModalView === 'details' && `Dealer Details - ${safeString(dealerDetails.dealer?.businessName)}`}
                        {unifiedModalView === 'vouchers' && `Vouchers - ${safeString(dealerDetails.dealer?.businessName)}`}
                        {unifiedModalView === 'pointBreakdown' && `Point Breakdown - Voucher ${safeString(selectedVoucher?.voucherNumber)}`}
                        {unifiedModalView === 'pointsDetails' && `Points Analysis - ${safeString(dealerDetails.dealer?.businessName)}`}
                      </h3>
                      
                      {/* Navigation Breadcrumb */}
                      <div className="flex items-center text-sm text-gray-500">
                        <button
                          onClick={goToDetails}
                          className={`hover:text-blue-600 ${unifiedModalView === 'details' ? 'text-blue-600 font-medium' : ''}`}
                        >
                          Details
                        </button>
                        <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <button
                          onClick={goToVouchers}
                          className={`hover:text-blue-600 ${unifiedModalView === 'vouchers' ? 'text-blue-600 font-medium' : ''}`}
                        >
                          Vouchers
                        </button>
                        <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <button
                          onClick={goToPointsDetails}
                          disabled={loadingPointsDetails}
                          className={`hover:text-blue-600 ${unifiedModalView === 'pointsDetails' ? 'text-blue-600 font-medium' : ''} ${loadingPointsDetails ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {loadingPointsDetails ? 'Loading...' : 'Points Analysis'}
                        </button>
                        {unifiedModalView === 'pointBreakdown' && (
                          <>
                            <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-blue-600 font-medium">Point Breakdown</span>
                          </>
                        )}
                        {unifiedModalView === 'pointsDetails' && (
                          <>
                            <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-blue-600 font-medium">Points Analysis</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={closeUnifiedModal}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Content Based on Current View */}
                  {unifiedModalView === 'details' && (
                    <div>
                      {/* Dealer Profile Section */}
                      <div className="bg-gray-50 p-4 rounded-lg mb-6">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Dealer Profile
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Business Name:</span>
                            <span className="ml-2 text-gray-900">{safeString(dealerDetails.dealer?.businessName)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Owner:</span>
                            <span className="ml-2 text-gray-900">{safeString(dealerDetails.dealer?.businessOwner)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Phone:</span>
                            <span className="ml-2 text-gray-900">{safeString(dealerDetails.dealer?.phone)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Dealer ID:</span>
                            <span className="ml-2 text-gray-900">{safeString(dealerDetails.dealer?.id)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">GST No:</span>
                            <span className="ml-2 text-gray-900">{safeString(dealerDetails.dealer?.gstNo)}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="font-medium text-gray-700">Address:</span>
                            <span className="ml-2 text-gray-900">{safeString(dealerDetails.dealer?.address)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Tally Ledger Summary */}
                      {dealerDetails.ledger && (
                        <div className="bg-blue-50 p-4 rounded-lg mb-6">
                          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Tally Ledger Summary
                          </h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Ledger Name:</span>
                              <span className="ml-2 text-gray-900">{safeString(dealerDetails.ledger.Name)}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Parent Group:</span>
                              <span className="ml-2 text-gray-900">{safeString(dealerDetails.ledger.parent)}</span>
                            </div>
                            {dealerDetails.ledger.GSTIN && (
                              <div>
                                <span className="font-medium text-gray-700">GSTIN:</span>
                                <span className="ml-2 text-gray-900">{safeString(dealerDetails.ledger.GSTIN)}</span>
                              </div>
                            )}
                            {dealerDetails.ledger.address && (
                              <div className="col-span-2">
                                <span className="font-medium text-gray-700">Ledger Address:</span>
                                <span className="ml-2 text-gray-900">{safeString(dealerDetails.ledger.address)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Summary Cards */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-blue-50 p-4 rounded-lg text-center">
                          <p className="text-2xl font-bold text-blue-600">
                        {formatMoney(safeNumber(dealerDetails.summary?.totalSales))}
                      </p>
                      <p className="text-sm text-blue-800">Total Sales</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {formatPoints(safeNumber(dealerDetails.summary?.totalPoints))}
                      </p>
                      <p className="text-sm text-purple-800">Total Points</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {safeString(dealerDetails.summary?.totalVouchers || 0)}
                      </p>
                      <p className="text-sm text-green-800">Total Vouchers</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex space-x-3">
                      <button
                        onClick={goToVouchers}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View Vouchers
                      </button>
                      
                      <button
                        onClick={goToPointsDetails}
                        disabled={loadingPointsDetails}
                        className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.950.690h4.915c.969 0 1.371 1.240.588 1.810l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.570-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.570-.380-1.810.588-1.810h4.914a1 1 0 00.951-.690l1.519-4.674z" />
                        </svg>
                        {loadingPointsDetails ? 'Loading...' : 'Points Analysis'}
                      </button>
                    </div>
                    
                    {dealerDetails.vouchers && dealerDetails.vouchers.length > 0 && (
                      <button
                        onClick={handleExportAllVouchers}
                        disabled={exportingDetails}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        <FaDownload className="mr-2" />
                        {exportingDetails ? 'Exporting...' : 'Export Vouchers'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {unifiedModalView === 'vouchers' && (
                <div>
                  {/* Vouchers List */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-gray-900">Vouchers</h4>
                      <button
                        onClick={() => fetchAllDealerVouchers(selectedDealerId)}
                        disabled={loadingDetails}
                        className="text-blue-600 hover:text-blue-800 text-sm hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        {loadingDetails ? 'Loading...' : 'Refresh Vouchers'}
                      </button>
                    </div>

                    {dealerDetails.vouchers && dealerDetails.vouchers.length > 0 ? (
                      <>
                        {/* Export Button */}
                        <div className="mb-4 flex justify-end">
                          <button
                            onClick={handleExportAllVouchers}
                            disabled={exportingDetails}
                            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            <FaDownload className="mr-2" />
                            {exportingDetails ? 'Exporting...' : 'Export All'}
                          </button>
                        </div>

                        {/* Vouchers Table */}
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Voucher #
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Date
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Amount
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Points
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Items
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {dealerDetails.vouchers.map((voucher, index) => (
                                <tr key={voucher._id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {safeString(voucher.voucherNumber) || '–––'}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    {formatDate(voucher.date)}
                                  </td>
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                    {formatMoney(safeNumber(voucher.totalAmount))}
                                  </td>
                                  <td className="px-4 py-3 text-sm font-medium text-purple-600">
                                    {formatPoints(safeNumber(voucher.totalPoints))}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    {safeString(voucher.items ? voucher.items.length : 0)} items
                                  </td>
                                  <td className="px-4 py-3 text-sm font-medium">
                                    <button
                                      onClick={() => handleViewPointsStructure(voucher)}
                                      className="text-blue-600 hover:text-blue-900 text-xs hover:underline transition-colors"
                                    >
                                      View Points
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg font-medium text-gray-900 mb-2">No vouchers found</p>
                        <p className="text-sm text-gray-500">No vouchers found for this dealer in the selected campaign period.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}


                  {unifiedModalView === 'pointBreakdown' && selectedVoucher && (
                    <div>
                      {/* Back Button */}
                      <div className="mb-4">
                        <button
                          onClick={goBackFromPointBreakdown}
                          className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Back to Vouchers
                        </button>
                      </div>

                      {/* Voucher Summary */}
                      <div className="bg-gray-50 p-4 rounded-lg mb-6">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Date:</span>
                            <span className="ml-2 text-gray-900">{formatDate(selectedVoucher.date)}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Total Amount:</span>
                            <span className="ml-2 text-gray-900">{formatMoney(safeNumber(selectedVoucher.totalAmount))}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Total Points:</span>
                            <span className="ml-2 text-purple-600 font-medium">{formatPoints(safeNumber(selectedVoucher.totalPoints))}</span>
                          </div>
                        </div>
                      </div>

                      {/* Items and Points Breakdown */}
                      {selectedVoucher.items && selectedVoucher.items.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Group</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rule</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points Earned</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {selectedVoucher.items.map((item, index) => (
                                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-4 py-3 text-sm text-gray-900">{safeString(item.itemName) || '–––'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{formatMoney(safeNumber(item.amount))}</td>
                                  <td className="px-4 py-3 text-sm text-gray-500">{safeString(item.stockGroupName) || safeString(item.stockGroup) || 'Default'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    {item.pointsRule ? 
                                      `${safeString(item.pointsRule.points)} pts per ₹${safeString(item.pointsRule.amount)}` : 
                                      item.points > 0 ? 'Points earned from campaign rules' : 'No points rule applied'
                                    }
                                  </td>
                                  <td className="px-4 py-3 text-sm font-medium text-purple-600">
                                    {formatPoints(safeNumber(item.points))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                          <p className="text-lg font-medium text-gray-900 mb-2">No item details available</p>
                          <p className="text-sm text-gray-500">No item details available for this voucher.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Points Details View */}
                  {unifiedModalView === 'pointsDetails' && dealerPointsDetails && (
                    <div>
                      {/* Back Button */}
                      <div className="mb-4">
                        <button
                          onClick={goBackFromPointsDetails}
                          className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Back to Details
                        </button>
                      </div>

                      {/* Points Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-blue-600">
                              {formatPoints(safeNumber(dealerPointsDetails.pointsBreakdown?.totalPoints))}
                            </p>
                            <p className="text-sm font-medium text-blue-700">Total Points</p>
                          </div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-green-600">
                              {formatMoney(safeNumber(dealerPointsDetails.pointsBreakdown?.totalSales))}
                            </p>
                            <p className="text-sm font-medium text-green-700">Total Sales</p>
                          </div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-purple-600">
                              {formatPoints(safeNumber(dealerPointsDetails.pointsBreakdown?.totalPointsFromRules))}
                            </p>
                            <p className="text-sm font-medium text-purple-700">Points from Rules</p>
                          </div>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-orange-600">
                              {Math.round(safeNumber(dealerPointsDetails.pointsBreakdown?.coveragePercentage))}%
                            </p>
                            <p className="text-sm font-medium text-orange-700">Rule Coverage</p>
                          </div>
                        </div>
                      </div>

                      {/* Points Breakdown Details */}
                      <div className="bg-gray-50 p-4 rounded-lg mb-6">
                        <h4 className="font-medium text-gray-900 mb-3">Points Analysis</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Points per Rupee:</span>
                            <span className="ml-2 text-gray-900">
                              {formatPoints(safeNumber(dealerPointsDetails.pointsBreakdown?.pointsPerRupee))}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Vouchers with Points:</span>
                            <span className="ml-2 text-gray-900">
                              {safeString(dealerPointsDetails.pointsBreakdown?.vouchersWithPoints)} of {safeString(dealerPointsDetails.pointsBreakdown?.totalVouchers)}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Average Points/Voucher:</span>
                            <span className="ml-2 text-gray-900">
                              {formatPoints(safeNumber(dealerPointsDetails.pointsBreakdown?.averagePointsPerVoucher))}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Sales Covered by Rules:</span>
                            <span className="ml-2 text-gray-900">
                              {formatMoney(safeNumber(dealerPointsDetails.pointsBreakdown?.salesCoveredByRules))}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Sales Not Covered:</span>
                            <span className="ml-2 text-gray-900">
                              {formatMoney(safeNumber(dealerPointsDetails.pointsBreakdown?.salesNotCoveredByRules))}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Points from Default:</span>
                            <span className="ml-2 text-gray-900">
                              {formatPoints(safeNumber(dealerPointsDetails.pointsBreakdown?.pointsFromDefaultRules))}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Stock Group Breakdown */}
                      {dealerPointsDetails.stockGroupBreakdown && dealerPointsDetails.stockGroupBreakdown.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                            <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            Stock Group Points Breakdown
                          </h4>
                          <div className="space-y-4">
                            {dealerPointsDetails.stockGroupBreakdown.map((sg, index) => (
                              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <h5 className="font-medium text-gray-900">{safeString(sg.stockGroupName)}</h5>
                                    <p className="text-sm text-gray-500">
                                      Rule: {formatPoints(sg.rulePoints)} points per {formatMoney(sg.ruleAmount)}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-lg font-semibold text-purple-600">
                                      {formatPoints(safeNumber(sg.totalPoints))}
                                    </p>
                                    <p className="text-sm text-gray-500">points</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <span className="font-medium text-gray-700">Sales:</span>
                                    <span className="ml-2 text-gray-900">{formatMoney(safeNumber(sg.totalSales))}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Vouchers:</span>
                                    <span className="ml-2 text-gray-900">{safeString(sg.voucherCount)}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-700">Items:</span>
                                    <span className="ml-2 text-gray-900">{safeString(sg.itemCount)}</span>
                                  </div>
                                </div>
                                
                                {/* Show top items if available */}
                                {sg.items && sg.items.length > 0 && (
                                  <div className="mt-4 pt-3 border-t border-gray-200">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Top Items:</p>
                                    <div className="space-y-2">
                                      {sg.items.slice(0, 3).map((item, itemIndex) => (
                                        <div key={itemIndex} className="flex justify-between items-center text-sm">
                                          <span className="text-gray-600 truncate">
                                            {safeString(item.itemName)} (Qty: {safeString(item.quantity)})
                                          </span>
                                          <span className="text-purple-600 font-medium">
                                            {formatPoints(safeNumber(item.points))} pts
                                          </span>
                                        </div>
                                      ))}
                                      {sg.items.length > 3 && (
                                        <p className="text-xs text-gray-500">
                                          ...and {sg.items.length - 3} more items
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Campaign Rules Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Campaign Rules Summary
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Total Rules:</span>
                            <span className="ml-2 text-gray-900">
                              {safeString(dealerPointsDetails.campaignRules?.totalRules)}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Rules with Activity:</span>
                            <span className="ml-2 text-gray-900">
                              {safeString(dealerPointsDetails.campaignRules?.rulesWithActivity)}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Default Points:</span>
                            <span className="ml-2 text-gray-900">
                              {formatPoints(safeNumber(dealerPointsDetails.campaignRules?.defaultPoints))}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Default Amount:</span>
                            <span className="ml-2 text-gray-900">
                              {formatMoney(safeNumber(dealerPointsDetails.campaignRules?.defaultAmount))}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dealer Stock Group Details Modal */}
        {showStockGroupDetailsModal && selectedStockGroupDetails && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="flex items-center justify-center min-h-full p-4">
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  {/* Modal Header */}
                  <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-4 border-b">
                    <h3 className="text-lg font-medium text-gray-900">
                      Stock Group Points - {safeString(selectedStockGroupDetails.dealer?.businessName)}
                    </h3>
                    <button
                      onClick={() => setShowStockGroupDetailsModal(false)}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Dealer Info */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Dealer Information
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Business Name:</span>
                        <span className="ml-2 text-gray-900">{safeString(selectedStockGroupDetails.dealer?.businessName)}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Phone:</span>
                        <span className="ml-2 text-gray-900">{safeString(selectedStockGroupDetails.dealer?.phone)}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Dealer ID:</span>
                        <span className="ml-2 text-gray-900">{safeString(selectedStockGroupDetails.dealer?.id)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {formatMoney(safeNumber(selectedStockGroupDetails.summary?.totalSalesFromRules))}
                      </p>
                      <p className="text-sm text-blue-800">Sales from Rules</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {formatPoints(safeNumber(selectedStockGroupDetails.summary?.totalPointsFromRules))}
                      </p>
                      <p className="text-sm text-purple-800">Points from Rules</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {safeString(selectedStockGroupDetails.summary?.stockGroupCount || 0)}
                      </p>
                      <p className="text-sm text-green-800">Active Stock Groups</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-orange-600">
                        {safeString(selectedStockGroupDetails.summary?.totalVouchersFromRules || 0)}
                      </p>
                      <p className="text-sm text-orange-800">Total Vouchers</p>
                    </div>
                  </div>

                  {/* Stock Groups Breakdown */}
                  <div className="space-y-6">
                    <h4 className="font-medium text-gray-900">Stock Group Points Breakdown</h4>
                    
                    {selectedStockGroupDetails?.stockGroups && selectedStockGroupDetails.stockGroups.length > 0 ? (
                      selectedStockGroupDetails.stockGroups.map((stockGroup, index) => (
                        <div key={stockGroup.stockGroupId || index} className="bg-white border border-gray-200 rounded-lg p-4">
                          {/* Stock Group Header */}
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h5 className="text-lg font-medium text-gray-900">{safeString(stockGroup.stockGroupName)}</h5>
                              <p className="text-sm text-gray-600">
                                Rule: {safeString(stockGroup.rulePoints)} points per ₹{safeString(stockGroup.ruleAmount)}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-semibold text-purple-600">
                                {formatPoints(safeNumber(stockGroup.totalPointsFromStockGroup))} points
                              </div>
                              <div className="text-sm text-gray-600">
                                {formatMoney(safeNumber(stockGroup.totalSalesFromStockGroup))} sales
                              </div>
                            </div>
                          </div>

                          {/* Vouchers for this stock group */}
                          {stockGroup.vouchers && stockGroup.vouchers.length > 0 && (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Voucher #
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Date
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Sales from Group
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Points from Group
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Items
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {stockGroup.vouchers.map((voucher, vIndex) => (
                                    <tr key={voucher.voucherId || vIndex} className={vIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-3 py-2 text-sm text-gray-900">
                                        {safeString(voucher.voucherNumber) || '–––'}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-500">
                                        {formatDate(voucher.date)}
                                      </td>
                                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                                        {formatMoney(safeNumber(voucher.salesFromStockGroup))}
                                      </td>
                                      <td className="px-3 py-2 text-sm font-medium text-purple-600">
                                        {formatPoints(safeNumber(voucher.pointsFromStockGroup))}
                                      </td>
                                      <td className="px-3 py-2 text-sm text-gray-500">
                                        {voucher.items ? voucher.items.length : 0} items
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <p className="text-lg font-medium text-gray-900 mb-2">No stock group activity found</p>
                        <p className="text-sm text-gray-500">This dealer has no sales activity from stock groups with rules in the selected campaign period.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stock Group Dealers Modal - (z-60) */}
        {showStockGroupDealersModal && selectedStockGroupDetails && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-60 overflow-y-auto h-full w-full z-60">
            <div className="flex items-center justify-center min-h-full p-4">
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-4 border-b">
                    <h3 className="text-lg font-medium text-gray-900">
                      Dealers Benefiting from {safeString(selectedStockGroupDetails.stockGroupName)}
                    </h3>
                    <button
                      onClick={() => setShowStockGroupDealersModal(false)}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {selectedStockGroupDetails.dealers && selectedStockGroupDetails.dealers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vouchers</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedStockGroupDetails.dealers.map((dealer, index) => (
                            <tr key={dealer.dealerId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{safeString(dealer.dealerName)}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{safeString(dealer.dealerPhone) || '–––'}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{formatMoney(safeNumber(dealer.sales))}</td>
                              <td className="px-4 py-3 text-sm font-medium text-purple-600">{formatPoints(safeNumber(dealer.points))}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{safeString(dealer.voucherCount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="text-lg font-medium text-gray-900 mb-2">No dealers found</p>
                      <p className="text-sm text-gray-500">No dealers found for this stock group.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stock Group Items Modal - (z-60) */}
        {showStockGroupItemsModal && selectedStockGroupItems && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-60 overflow-y-auto h-full w-full z-60">
            <div className="flex items-center justify-center min-h-full p-4">
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-4 border-b">
                    <h3 className="text-lg font-medium text-gray-900">
                      Items Sold from {safeString(selectedStockGroupItems.stockGroupName)}
                    </h3>
                    <button
                      onClick={() => setShowStockGroupItemsModal(false)}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {selectedStockGroupItems.items && selectedStockGroupItems.items.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Sales</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points Generated</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Times Sold</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedStockGroupItems.items.map((item, index) => (
                            <tr key={item.itemId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{safeString(item.itemName)}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{formatMoney(safeNumber(item.totalSales))}</td>
                              <td className="px-4 py-3 text-sm font-medium text-purple-600">{formatPoints(safeNumber(item.totalPoints))}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{safeString(item.salesCount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <p className="text-lg font-medium text-gray-900 mb-2">No items found</p>
                      <p className="text-sm text-gray-500">No items found for this stock group.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dealer Search Modal */}
        {showDealerSearchModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="flex items-center justify-center min-h-full p-4">
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  {/* Modal Header */}
                  <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Search for Specific Dealer
                    </h3>
                    <button
                      onClick={handleCloseDealerSearchModal}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Search Section */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search by dealer name, ID, phone, owner, or GSTIN
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={dealerSearchQuery}
                        onChange={(e) => handleDealerSearch(e.target.value)}
                        placeholder="e.g., RAJASTHAN LIME UDYOG, RLU1, phone number..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        autoFocus
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      {loadingSearch && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Type at least 2 characters to start searching
                    </p>
                  </div>

                  {/* Search Results */}
                  <div className="space-y-4">
                    {dealerSearchQuery.length >= 2 && (
                      <>
                        {loadingSearch ? (
                          <div className="text-center py-8">
                            <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                            <p className="text-gray-500">Searching dealers...</p>
                          </div>
                        ) : searchResults.length > 0 ? (
                          <>
                            <div className="text-sm text-gray-600 mb-4">
                              Found {searchResults.length} dealer{searchResults.length !== 1 ? 's' : ''} matching "{dealerSearchQuery}"
                            </div>
                            <div className="max-h-96 overflow-y-auto space-y-3">
                              {searchResults.map((dealer, index) => (
                                <div
                                  key={dealer.dealerId || index}
                                  onClick={() => handleSelectDealerFromSearch(dealer)}
                                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-gray-900 mb-1">
                                        {dealer.dealerName}
                                      </h4>
                                      <div className="text-sm text-gray-600 space-y-1">
                                        <div><strong>ID:</strong> {dealer.dealerUniqueId}</div>
                                        {dealer.dealerPhone && (
                                          <div><strong>Phone:</strong> {dealer.dealerPhone}</div>
                                        )}
                                        {dealer.dealerOwner && (
                                          <div><strong>Owner:</strong> {dealer.dealerOwner}</div>
                                        )}
                                        {dealer.dealerGstin && (
                                          <div><strong>GSTIN:</strong> {dealer.dealerGstin}</div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right text-sm">
                                      <div className="font-semibold text-gray-900">
                                        {formatMoney(dealer.totalSales)}
                                      </div>
                                      <div className="text-purple-600 font-medium">
                                        {formatPoints(dealer.totalPoints)} points
                                      </div>
                                      <div className="text-gray-500">
                                        {dealer.voucherCount} voucher{dealer.voucherCount !== 1 ? 's' : ''}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : dealerSearchQuery.length >= 2 ? (
                          <div className="text-center py-8">
                            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <p className="text-gray-500 text-lg font-medium">No dealers found</p>
                            <p className="text-gray-400 text-sm mt-1">
                              No dealers match "{dealerSearchQuery}". Try a different search term.
                            </p>
                          </div>
                        ) : null}
                      </>
                    )}

                    {dealerSearchQuery.length < 2 && (
                      <div className="text-center py-12">
                        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p className="text-gray-500 text-lg font-medium">Search for a dealer</p>
                        <p className="text-gray-400 text-sm mt-1">
                          Enter dealer name, ID, phone, owner name, or GSTIN to find specific dealers
                        </p>
                        <div className="mt-4 text-xs text-gray-400">
                          <p>Examples: "RAJASTHAN LIME UDYOG", "RLU1", "9876543210"</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                    <button
                      onClick={handleCloseDealerSearchModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderCompanySelection = () => {
    const availableCompanies = getCompaniesForSelection();
    
    console.log('Rendering company selection:', {
      totalCompanies: companies.length,
      availableCompanies: availableCompanies.length,
      selectedCompany: selectedCompany,
      user: user
    });

    return (
      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
          Company: {availableCompanies.length === 0 && companies.length === 0 && (
            <span className="text-red-500 text-xs ml-1">(Loading...)</span>
          )}
        </label>
        <select
          id="company"
          value={selectedCompany}
          onChange={handleCompanyChange}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          disabled={availableCompanies.length === 0}
        >
          <option value="">
            {availableCompanies.length === 0 ? 'Loading companies...' : 'Select Company'}
          </option>
          {availableCompanies.map(company => (
            <option key={company._id} value={company._id}>
              {safeString(company.name || company.companyName || company.title || 'Unknown Company')}
            </option>
          ))}
        </select>
        {/* Debug info - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="text-xs text-gray-500 mt-1">
            Debug: {companies.length} total, {availableCompanies.length} available, selected: {selectedCompany || 'none'}
          </div>
        )}
      </div>
    );
  };

  // Update the campaign selection rendering to show loading state
  const renderCampaignSelection = () => {
    return (
      <div>
        <label htmlFor="campaign" className="block text-sm font-medium text-gray-700 mb-2">
          Campaign: {!selectedCompany && (
            <span className="text-gray-500 text-xs ml-1">(Select a company first)</span>
          )}
        </label>
        <select
          id="campaign"
          value={selectedCampaign}
          onChange={handleCampaignChange}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          disabled={!selectedCompany}
        >
          <option value="">
            {!selectedCompany ? 'Select a company first' : 
             campaigns.length === 0 ? 'Loading campaigns...' : 
             'Select Campaign'}
          </option>
          {campaigns.map(campaign => (
            <option key={campaign._id} value={campaign._id}>
              {safeString(campaign.name)}
            </option>
          ))}
        </select>
        {/* Debug info - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="text-xs text-gray-500 mt-1">
            Debug: Company: {selectedCompany || 'none'}, Campaigns: {campaigns.length}, Selected: {selectedCampaign || 'none'}
          </div>
        )}
      </div>
    );
  };

  // Add missing handleViewStockGroupDetails function
  const handleViewStockGroupDetails = async (stockGroup) => {
    if (!stockGroup || !selectedCampaign) {
      showAlert('warning', 'Please select a stock group and campaign');
      return;
    }
    
    setLoadingDetails(true);
    setApiRequestState('connecting');
    setRequestProgress(10);
    
    try {
      setApiRequestState('receiving');
      setRequestProgress(50);
      
      const response = await api.get(`/reports/dealer-points/stock-group-details/${stockGroup.id || stockGroup._id}`, {
        params: {
          companyID: selectedCompany,
          campaignId: selectedCampaign
        }
      });
      
      setApiRequestState('processing');
      setRequestProgress(80);
      
      if (response.data.success) {
        setSelectedStockGroupDetails(response.data.data);
        setShowStockGroupDetailsModal(true);
        setApiRequestState('complete');
        setRequestProgress(100);
      } else {
        throw new Error(response.data.msg || 'Failed to fetch stock group details');
      }
    } catch (error) {
      console.error('Error fetching stock group details:', error);
      setApiRequestState('error');
      showAlert('error', `Failed to fetch stock group details: ${error.message}`);
    } finally {
      setLoadingDetails(false);
      setTimeout(() => {
        setApiRequestState('idle');
        setRequestProgress(0);
      }, 1000);
    }
  };

  // Main component return
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {loadingCompanyInfo ? (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Retrieving company access information...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Campaign Reports</h1>
            <p className="text-sm text-gray-600 mt-1">
              View and analyze dealer performance and stock group points across campaigns
            </p>
          </div>

      {/* Company and Campaign Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {renderCompanySelection()}
        {renderCampaignSelection()}
      </div>

      {/* Report Type Selection and Export Button - Inline */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Report Type:</label>
            <div className="flex space-x-4">
              <button
                onClick={() => handleReportTypeChange('dealer_performance')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  reportType === 'dealer_performance'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Dealer Performance
              </button>
              <button
                onClick={() => handleReportTypeChange('stock_group_points')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  reportType === 'stock_group_points'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Stock Group Points
              </button>
            </div>
          </div>
          
          {/* Action Buttons */}
          {selectedCompany && selectedCampaign && (
            <div className="flex flex-wrap gap-2">
              {/* Get Specific Dealer Report Button - Available for dealer performance */}
              {reportType === 'dealer_performance' && (
                <button
                  onClick={handleOpenDealerSearchModal}
                  disabled={loading}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  } transition-colors duration-200`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Get Specific Dealer Report
                </button>
              )}

              {/* Export Buttons */}
              {reportType === 'dealer_performance' && (
                <button
                  onClick={handleExportAllDealerPerformance}
                  disabled={exporting || loading}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    exporting || loading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                  } transition-colors duration-200`}
                >
                  <FaDownload className="mr-2" />
                  {exporting ? 'Exporting...' : 'Export All Dealer Performance'}
                </button>
              )}
              
              {reportType === 'stock_group_points' && (
                <button
                  onClick={handleExportAllStockGroupPoints}
                  disabled={exporting || loadingStockGroups}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    exporting || loadingStockGroups 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
                  } transition-colors duration-200`}
                >
                  <FaDownload className="mr-2" />
                  {exporting ? 'Exporting...' : 'Export All Stock Group Points'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {renderSummaryCards()}

          {/* Report Content */}
          {reportType === 'dealer_performance' && renderDealerTable()}
          {reportType === 'stock_group_points' && renderStockGroupPointsReport()}

          {/* Modals */}
          {renderModals()}
        </>
      )}
    </div>
  );
}